import React from 'react';
import { i18n } from 'config/locales';

import {
  CommonScrollIntoView,
  CommonOnError,
  CommonOnUpdate,
  CommonRequired,
  CommonAddressBooks,
} from 'types/props';
import ValidationElement, {
  ValidationElementProps,
} from 'components/Form/ValidationElement/ValidationElement';
import { MilitaryForeignItemContactModel } from 'forms/sections/military/foreign/types';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import { Dispatch } from 'redux';
import Field from 'components/Form/Field';
import Name from 'components/Form/Name';
import DateRange from 'components/Form/DateRange';
import Location from 'components/Form/Location';
import Text from 'components/Form/Text';
import Textarea from 'components/Form/Textarea';
import Comments from 'components/Form/Comments';

interface Props extends ValidationElementProps, Partial<MilitaryForeignItemContactModel> {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  className?: string
  scrollIntoView?: CommonScrollIntoView
  required?: CommonRequired
  attribute?: string
  dispatch?: Dispatch
  addressBooks?: CommonAddressBooks
}

export default class ForeignContact extends ValidationElement<Props> {
  handleNameError = (value, arr: ErrorCode[]) => {
    const arr2 = arr.map((err) => ({
      ...err,
      code: err.code.includes('required')
        ? `name${err.code.substring(err.code.lastIndexOf('.'))}`
        : err.code,
    }));
    return this.props.onError(value, arr2);
  };

  update = (queue: Partial<MilitaryForeignItemContactModel>) => {
    this.props.onUpdate({
      Name: this.props.Name,
      Address: this.props.Address,
      Title: this.props.Title,
      Dates: this.props.Dates,
      Frequency: this.props.Frequency,
      Comments: this.props.Comments,
      ...queue,
    });
  };

  updateName = (value: MilitaryForeignItemContactModel['Name']) => {
    this.update({
      Name: value,
    });
  };

  updateAddress = (value: MilitaryForeignItemContactModel['Address']) => {
    this.update({
      Address: value,
    });
  };

  updateTitle = (value: MilitaryForeignItemContactModel['Title']) => {
    this.update({
      Title: value,
    });
  };

  updateDates = (value: MilitaryForeignItemContactModel['Dates']) => {
    this.update({
      Dates: value,
    });
  };

  updateFrequency = (value: MilitaryForeignItemContactModel['Frequency']) => {
    this.update({
      Frequency: value,
    });
  };

  updateComments = (value: MilitaryForeignItemContactModel['Comments']) => {
    this.update({
      Comments: value,
    });
  };

  render() {
    const attribute = `${this.props.attribute}.List.items[${this.props.$index}].Item`;

    return (
      <div className="foreign-contact">
        <Field
          title={i18n.t('military.foreign.heading.contact.name')}
          optional
          filterErrors={Name.requiredErrorsOnly}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Name
            attribute={`${attribute}.Name`}
            name="Name"
            className="foreign-contact-name"
            aria-label="Provide the contact's full name"
            {...this.props.Name}
            onUpdate={this.updateName}
            onError={this.handleNameError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          />
        </Field>

        <Field
          attribute={`${attribute}.Address`}
          title={i18n.t('military.foreign.heading.contact.address')}
          optional
          help="military.foreign.help.contact.address"
          adjustFor="address"
          shrink
          scrollIntoView={this.props.scrollIntoView}
        >
          <Location
            attribute={`${attribute}.Address`}
            name="Address"
            className="foreign-contact-address"
            {...this.props.Address}
            addressBooks={this.props.addressBooks}
            addressBook="ForeignNational"
            dispatch={this.props.dispatch}
            layout={Location.ADDRESS}
            geocode
            showPostOffice={false}
            onUpdate={this.updateAddress}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t('military.foreign.heading.contact.title')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Title`}
        >
          <Text
            name="Title"
            {...this.props.Title}
            className="foreign-contact-title"
            maxlength="100"
            onUpdate={this.updateTitle}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Title`}
          />
        </Field>

        <Field
          title={i18n.t('military.foreign.heading.contact.dates')}
          help="military.foreign.help.contact.dates"
          adjustFor="daterange"
          shrink
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Dates`}
        >
          <DateRange
            name="Dates"
            className="foreign-contact-dates"
            {...this.props.Dates}
            minDateEqualTo
            onUpdate={this.updateDates}
            onError={this.props.onError}
            required={this.props.required}
            hideFromDay
            hideToDay
            attribute={`${attribute}.Dates`}
          />
        </Field>

        <Field
          attribute={`${attribute}.Frequency`}
          title={i18n.t('military.foreign.heading.contact.frequency')}
          help="military.foreign.help.contact.frequency"
          scrollIntoView={this.props.scrollIntoView}
        >
          <Text
            attribute={`${attribute}.Frequency`}
            name="Frequency"
            {...this.props.Frequency}
            className="foreign-contact-frequency"
            maxlength="100"
            onUpdate={this.updateFrequency}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>
        <Comments
          title={i18n.t('military.foreign.heading.contact.comments')}
          name="Comments"
          attribute="Comments"
          className="optional-comments"
          {...this.props.Comments}
          onUpdate={this.updateComments}
          onError={this.props.onError}
          required={false}
        >
        </Comments>
      </div>
    );
  }
}

ForeignContact.defaultProps = {
  Name: {},
  Address: {},
  Title: {},
  Dates: {},
  Frequency: {},
  Comments: {},
  addressBooks: {},
  dispatch: (action) => { },
  onUpdate: (queue) => { },
  onError: (value, arr) => arr,
};
